/*
 * LOCAL-ONLY: This file is a local-only addition to fluxer_app and will never exist upstream.
 * It renders the side panel for the message relocation feature. The panel reflects SelectMode
 * store state; message click handling (setAnchor / setHead, Shift+click for the end) is wired
 * separately in message components.
 *
 * The flow (relocation-plan.md §7): selection with a count, destination picker in sidebar order with
 * "Parent > Child" paths (searchable by path), a confirmation with the server's count (confirmMoveLines; no warning
 * about the destination's name, by the user's decision 2026-10-30), batches of 500 with progress and a stop, the
 * result with an Open link, and Recent moves with Undo and Resume. Every string is in RelocationStrings.ts, word for word what Obyrus shows.
 *
 * Lines to check on upstream merge: none — exclude this file entirely from any upstream sync.
 */

// SPDX-License-Identifier: AGPL-3.0-or-later

import {OutlineFrame} from '@app/features/app/components/layout/OutlineFrame';
// LOCAL-ONLY: the destination channel dropdown's parent category suffix uses .quoteSource, a
// verbatim copy of the (upstream-deleted) splash screen's quote attribution style kept in our own
// module — see destChannelOptions / renderOption below. Exclude from upstream sync.
import styles from '@app/features/channel/components/channel_view/SelectModePanel.module.css';
import {formatRecentOrFallback} from '@app/features/channel/components/guild_members_page/GuildMembersPageFormatting';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {relocationServerMatches} from '@app/features/channel/state/RelocationServerFilter';
import {
	confirmMoveLines,
	confirmUndoText,
	EMPTY_RANGE,
	fileFailuresText,
	logMetaText,
	movingBackText,
	NOT_MOVABLE_LINE,
	progressText,
	RelocationStrings,
} from '@app/features/channel/state/RelocationStrings';
import SelectMode, {RELOCATION_BATCH_SIZE, type RelocateLogEntry} from '@app/features/channel/state/SelectMode';
import {selectModeDestinations} from '@app/features/channel/utils/ChannelTreeLabels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import {channelLabel, relocationResultText, selectionCountText} from '@app/features/channel/utils/RelocationLabels';
import type {Guild} from '@app/features/guild/models/Guild';
import Guilds from '@app/features/guild/state/Guilds';
import MessagingMessages from '@app/features/messaging/state/MessagingMessages';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import {Button} from '@app/features/ui/button/Button';
import {Combobox, type ComboboxOption} from '@app/features/ui/components/form/FormCombobox';
import {Scroller} from '@app/features/ui/components/Scroller';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {ME} from '@fluxer/constants/src/AppConstants';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const DMS_DEST_VALUE = 'dms';
const PREVIEW_MAX_LENGTH = 80;
const S = RelocationStrings;

// LOCAL-ONLY: message preview helper for the anchor/head sections below — exclude from upstream sync.
function getMessagePreview(channelId: string | null, messageId: string | null): string | null {
	if (channelId == null || messageId == null) {
		return null;
	}
	const message = MessagingMessages.getMessages(channelId).get(messageId);
	if (!message?.content) {
		return null;
	}
	return message.content.length > PREVIEW_MAX_LENGTH
		? `${message.content.slice(0, PREVIEW_MAX_LENGTH)}...`
		: message.content;
}

function logStatus(entry: RelocateLogEntry): string | null {
	if (entry.state === 'started') return RelocationStrings.stoppedPartWay;
	if (entry.kind === 'undo') return RelocationStrings.movedBack;
	if (entry.undone === 'all') return RelocationStrings.undone;
	if (entry.undone === 'partly') return RelocationStrings.partlyUndone;
	return null;
}

/*
 * LOCAL-ONLY: destination channel options carry the channel's own label and its parent category path as separate
 * fields so the Combobox's renderOption can style the two parts differently. `label` stays the channel name alone:
 * it is the text of the closed input. The search matches the path too (filterOption below, F9), so "Archive > trash"
 * finds the right one of several #trash channels. Exclude from upstream sync.
 */
interface DestChannelOption extends ComboboxOption {
	channelLabel: string;
	categoryName: string | null;
}

interface SelectModePanelProps {
	guild?: Guild | null;
	channel: Channel;
}

export const SelectModePanel = observer(function SelectModePanel({guild, channel}: SelectModePanelProps) {
	const {i18n} = useLingui();
	/*
	 * LOCAL-ONLY: the destination server is read from SelectMode rather than held as component
	 * state so it survives the mobile cycle of closing the panel to tap messages and reopening it
	 * to submit — component state is lost on that unmount, the store is not. A null in the store
	 * means "the user has not picked a server yet", so we fall back to this channel's own context.
	 * Exclude from upstream sync.
	 */
	const destGuildId: string | typeof DMS_DEST_VALUE | null =
		SelectMode.destGuildId ?? (guild ? guild.id : DMS_DEST_VALUE);

	const guilds = Guilds.getGuilds();
	// LOCAL-ONLY (nested-ordering-plan.md B21): a server's channels in sidebar (tree) order, each with the
	// "Parent > Child" path of its categories. Exclude from upstream sync.
	const guildDestinations =
		destGuildId != null && destGuildId !== DMS_DEST_VALUE ? selectModeDestinations(destGuildId, channel.id) : [];
	const parentPathById = new Map(guildDestinations.map((d) => [d.channel.id, d.parentPath]));
	let destChannels: Array<Channel> = [];
	if (destGuildId === DMS_DEST_VALUE) destChannels = Channels.dmChannels.filter((c) => c.id !== channel.id);
	else if (destGuildId != null) destChannels = guildDestinations.map((d) => d.channel);

	const handleDestGuildChange = (value: string) => {
		SelectMode.setDestGuildId(value === DMS_DEST_VALUE ? DMS_DEST_VALUE : value || null);
	};

	const destGuildOptions: Array<ComboboxOption> = [
		{value: DMS_DEST_VALUE, label: S.directMessages},
		...guilds.map((g) => ({value: g.id, label: g.name || ''})),
	];
	const destChannelOptions: Array<DestChannelOption> = destChannels.map((c) => {
		const label = destGuildId === DMS_DEST_VALUE ? ChannelUtils.getDMDisplayName(c) : `#${c.name}`;
		const categoryName = destGuildId === DMS_DEST_VALUE ? null : (parentPathById.get(c.id) ?? null);
		return {value: c.id, label, channelLabel: label, categoryName};
	});

	const anchorPreview = getMessagePreview(SelectMode.channelId, SelectMode.anchorId);
	const headPreview = getMessagePreview(SelectMode.channelId, SelectMode.headId);
	const canReset = (SelectMode.anchorId != null || SelectMode.headId != null) && !SelectMode.busy;
	const hasRange = SelectMode.anchorId != null && SelectMode.headId != null;

	// The destination as the confirmation names it: "#name (Server > Parent > Child)", or the DM's name.
	const destChannel = SelectMode.destChannelId ? Channels.getChannel(SelectMode.destChannelId) : null;
	const destGuildName = destChannel?.guildId ? Guilds.getGuild(destChannel.guildId)?.name : null;
	const destPath = destChannel ? parentPathById.get(destChannel.id) : null;
	let destDescription = '';
	if (destChannel?.guildId) {
		destDescription = `#${destChannel.name} (${[destGuildName, destPath].filter(Boolean).join(' > ')})`;
	} else if (destChannel) {
		destDescription = ChannelUtils.getDMDisplayName(destChannel);
	}
	const confirm = SelectMode.confirm;
	const progress = SelectMode.progress;
	const result = SelectMode.result;

	const frameSides = guild && !MobileLayout.enabled ? {left: false} : undefined;

	const openChannel = (channelId: string) => {
		const target = Channels.getChannel(channelId);
		NavigationCommands.selectChannel(target?.guildId ?? ME, channelId);
	};

	const renderResult = () => {
		if (!result) return null;
		const text = relocationResultText(result);
		return (
			<div className={styles.success} data-flx="channel.channel-view.select-mode-panel.success">
				<span>{text}</span>
				{result.fileFailures > 0 && <span> {fileFailuresText(result.fileFailures)}</span>}
				{result.movedCount > 0 && result.destChannelId !== channel.id && (
					<Button
						type="button"
						variant="secondary"
						small
						onClick={() => openChannel(result.destChannelId)}
						data-flx="channel.channel-view.select-mode-panel.open-destination"
					>
						{S.open}
					</Button>
				)}
			</div>
		);
	};

	const renderLogEntry = (entry: RelocateLogEntry) => {
		const status = logStatus(entry);
		const canUndo = entry.kind !== 'undo' && entry.state !== 'started' && entry.undone !== 'all';
		const canResume = entry.state === 'started' && entry.unfinishedLogId != null;
		return (
			<div key={entry.logId} className={styles.logEntry} data-flx="channel.channel-view.select-mode-panel.log-entry">
				<span className={styles.logRoute} data-flx="channel.channel-view.select-mode-panel.log-route">
					{channelLabel(entry.sourceChannel.id, entry.sourceChannel.name)} →{' '}
					{channelLabel(entry.destChannel.id, entry.destChannel.name)}
				</span>
				<span className={styles.logMeta} data-flx="channel.channel-view.select-mode-panel.log-meta">
					{logMetaText(
						entry.movedCount,
						entry.performedBy.displayName ?? entry.performedBy.id,
						formatRecentOrFallback(new Date(entry.createdAt), i18n),
						entry.batches ?? 1,
					)}
					{status != null && ` · ${status}`}
				</span>
				{(canUndo || canResume) && (
					<span className={styles.logActions} data-flx="channel.channel-view.select-mode-panel.log-actions">
						{canResume && (
							<Button
								type="button"
								variant="secondary"
								small
								disabled={SelectMode.busy}
								onClick={() => void SelectMode.resume(entry)}
								data-flx="channel.channel-view.select-mode-panel.log-resume"
							>
								{S.resume}
							</Button>
						)}
						{canUndo && (
							<Button
								type="button"
								variant="secondary"
								small
								disabled={SelectMode.busy}
								onClick={() => SelectMode.requestUndo(entry)}
								data-flx="channel.channel-view.select-mode-panel.log-undo"
							>
								{S.undo}
							</Button>
						)}
					</span>
				)}
			</div>
		);
	};

	return (
		<OutlineFrame hideTopBorder sides={frameSides}>
			<aside className={styles.container} data-flx="channel.channel-view.select-mode-panel.container">
				<Scroller
					className={styles.scrollerPadding}
					contentClassName={styles.scrollerContent}
					data-flx="channel.channel-view.select-mode-panel.scroller"
				>
					{/* LOCAL-ONLY (relocation parity, 2026-10-30): the title, and on desktop the one close action. At phone
					    width the selection bar closes relocation; this panel is its sheet. No selection switch: selection is
					    on while relocation is open. */}
					<div className={styles.titleRow} data-flx="channel.channel-view.select-mode-panel.title-row">
						<span className={styles.title} data-flx="channel.channel-view.select-mode-panel.title">
							{S.relocateMessages}
						</span>
						{!MobileLayout.enabled && (
							<Button
								type="button"
								variant="secondary"
								small
								disabled={SelectMode.busy}
								onClick={SelectMode.deactivate}
								data-flx="channel.channel-view.select-mode-panel.close"
							>
								{S.closeRelocation}
							</Button>
						)}
					</div>

					<div className={styles.section} data-flx="channel.channel-view.select-mode-panel.anchor-section">
						<span className={styles.fieldLabel} data-flx="channel.channel-view.select-mode-panel.anchor-label">
							{S.startLabel}
						</span>
						{SelectMode.anchorId != null ? (
							<>
								<span className={styles.idValue} data-flx="channel.channel-view.select-mode-panel.anchor-value">
									{SelectMode.anchorId}
								</span>
								{anchorPreview != null && (
									<span className={styles.preview} data-flx="channel.channel-view.select-mode-panel.anchor-preview">
										{anchorPreview}
									</span>
								)}
							</>
						) : (
							<span className={styles.placeholder} data-flx="channel.channel-view.select-mode-panel.anchor-placeholder">
								{S.startPlaceholder}
							</span>
						)}
					</div>

					<div className={styles.section} data-flx="channel.channel-view.select-mode-panel.head-section">
						<span className={styles.fieldLabel} data-flx="channel.channel-view.select-mode-panel.head-label">
							{S.endLabel}
						</span>
						{SelectMode.headId != null ? (
							<>
								<span className={styles.idValue} data-flx="channel.channel-view.select-mode-panel.head-value">
									{SelectMode.headId}
								</span>
								{headPreview != null && (
									<span className={styles.preview} data-flx="channel.channel-view.select-mode-panel.head-preview">
										{headPreview}
									</span>
								)}
							</>
						) : (
							<span className={styles.placeholder} data-flx="channel.channel-view.select-mode-panel.head-placeholder">
								{S.endPlaceholder}
							</span>
						)}
					</div>

					{hasRange && (
						<span className={styles.selectedCount} data-flx="channel.channel-view.select-mode-panel.selected-count">
							{selectionCountText(SelectMode.selectionCount)}
						</span>
					)}

					{!MobileLayout.enabled && (
						<Button
							type="button"
							variant="secondary"
							onClick={SelectMode.reset}
							disabled={!canReset}
							small
							data-flx="channel.channel-view.select-mode-panel.reset-button"
						>
							{S.resetSelection}
						</Button>
					)}

					<Combobox
						id="select-mode-dest-server"
						label={
							<span className={styles.fieldLabel} data-flx="channel.channel-view.select-mode-panel.dest-server-label">
								{S.destServerLabel}
							</span>
						}
						value={destGuildId ?? ''}
						options={destGuildOptions}
						// LOCAL-ONLY (relocation-plan.md §21): the search trims the query, as the channel search below does.
						filterOption={(option, query) => relocationServerMatches(option.label, query)}
						onChange={handleDestGuildChange}
						data-flx="channel.channel-view.select-mode-panel.dest-server-select"
					/>

					<Combobox
						id="select-mode-dest"
						label={
							<span className={styles.fieldLabel} data-flx="channel.channel-view.select-mode-panel.dest-channel-label">
								{S.destChannelLabel}
							</span>
						}
						value={SelectMode.destChannelId ?? ''}
						options={destChannelOptions}
						// LOCAL-ONLY (relocation-plan.md F9): the search matches the channel name and its category path.
						filterOption={(option, query) => {
							const needle = query.trim().toLowerCase();
							if (!needle) return true;
							const path = option.data.categoryName ? `${option.data.categoryName} > ${option.label}` : option.label;
							return path.toLowerCase().includes(needle) || option.label.toLowerCase().includes(needle);
						}}
						// LOCAL-ONLY: channel name stays primary; the parent category path is appended in the old splash
						// quote attribution style (smaller, muted). Exclude from upstream sync.
						renderOption={(option) =>
							option.categoryName != null ? (
								<span
									className={styles.destOptionRow}
									data-flx="channel.channel-view.select-mode-panel.dest-option-row"
								>
									{option.channelLabel}
									<span
										className={styles.quoteSource}
										data-flx="channel.channel-view.select-mode-panel.dest-option-category"
									>
										{' - '}
										{option.categoryName}
									</span>
								</span>
							) : (
								option.channelLabel
							)
						}
						onChange={(value) => SelectMode.setDestChannelId(value || null)}
						placeholder={S.destChannelPlaceholder}
						data-flx="channel.channel-view.select-mode-panel.dest-select"
					/>

					{confirm != null && (
						<div className={styles.confirmBox} data-flx="channel.channel-view.select-mode-panel.confirm">
							{confirm.notMovable > 0 && <span className={styles.error}>{NOT_MOVABLE_LINE}</span>}
							{confirm.notMovable === 0 && confirm.count === 0 && <span>{EMPTY_RANGE}</span>}
							{confirm.notMovable === 0 &&
								confirm.count > 0 &&
								confirmMoveLines(confirm.count, channelLabel(channel.id), destDescription, RELOCATION_BATCH_SIZE).map(
									(line, index) =>
										index === 0 ? (
											<span key={line} data-flx="channel.channel-view.select-mode-panel.confirm-text">
												{line}
											</span>
										) : (
											<span key={line} className={styles.preview}>
												{line}
											</span>
										),
								)}
							<span className={styles.buttonRow}>
								{confirm.notMovable === 0 && confirm.count > 0 && (
									<Button
										type="button"
										variant="primary"
										small
										onClick={() => void SelectMode.confirmRelocate()}
										data-flx="channel.channel-view.select-mode-panel.confirm-relocate"
									>
										{S.relocate}
									</Button>
								)}
								<Button
									type="button"
									variant="secondary"
									small
									onClick={SelectMode.cancelConfirm}
									data-flx="channel.channel-view.select-mode-panel.confirm-cancel"
								>
									{S.cancel}
								</Button>
							</span>
						</div>
					)}

					{SelectMode.undoConfirm != null && (
						<div className={styles.confirmBox} data-flx="channel.channel-view.select-mode-panel.undo-confirm">
							<span>
								{confirmUndoText(
									SelectMode.undoConfirm.movedCount,
									channelLabel(SelectMode.undoConfirm.destChannel.id, SelectMode.undoConfirm.destChannel.name),
									channelLabel(SelectMode.undoConfirm.sourceChannel.id, SelectMode.undoConfirm.sourceChannel.name),
								)}
							</span>
							<span className={styles.buttonRow}>
								<Button
									type="button"
									variant="primary"
									small
									onClick={() => void SelectMode.confirmUndo()}
									data-flx="channel.channel-view.select-mode-panel.undo-confirm-button"
								>
									{S.undo}
								</Button>
								<Button
									type="button"
									variant="secondary"
									small
									onClick={SelectMode.cancelUndo}
									data-flx="channel.channel-view.select-mode-panel.undo-cancel"
								>
									{S.cancel}
								</Button>
							</span>
						</div>
					)}

					{progress != null && (
						<div className={styles.confirmBox} data-flx="channel.channel-view.select-mode-panel.progress">
							<span>
								{progress.undo ? movingBackText(progress.moved) : progressText(progress.moved, progress.total)}
							</span>
							{!progress.undo && (
								<Button
									type="button"
									variant="secondary"
									small
									disabled={SelectMode.stopRequested}
									onClick={SelectMode.requestStop}
									data-flx="channel.channel-view.select-mode-panel.stop"
								>
									{S.stopAfterBatch}
								</Button>
							)}
						</div>
					)}

					{renderResult()}

					{SelectMode.lastError != null && (
						<div className={styles.error} data-flx="channel.channel-view.select-mode-panel.error">
							{SelectMode.lastError}
						</div>
					)}

					<Button
						type="button"
						variant="primary"
						onClick={() => void SelectMode.requestRelocate()}
						disabled={!SelectMode.canSubmit || confirm != null}
						submitting={SelectMode.busy}
						fitContainer
						data-flx="channel.channel-view.select-mode-panel.relocate-button"
					>
						{SelectMode.counting ? S.counting : S.relocate}
					</Button>

					<div className={styles.logSection} data-flx="channel.channel-view.select-mode-panel.log-section">
						<span className={styles.fieldLabel} data-flx="channel.channel-view.select-mode-panel.log-label">
							{S.recentMoves}
						</span>
						{SelectMode.recentLog.length > 0 && (
							<div className={styles.logList} data-flx="channel.channel-view.select-mode-panel.log-list">
								{SelectMode.recentLog.slice(0, 5).map(renderLogEntry)}
							</div>
						)}
						{SelectMode.recentLog.length === 0 && (
							<span
								className={styles.placeholder}
								data-flx={`channel.channel-view.select-mode-panel.${SelectMode.logLoading ? 'log-loading' : 'log-empty'}`}
							>
								{SelectMode.logLoading ? S.loading : S.noRecentMoves}
							</span>
						)}
					</div>
				</Scroller>
			</aside>
		</OutlineFrame>
	);
});
